import React, { useState, useEffect } from "react";
import { getPage } from "../api/storefront";
import BlockRenderer from "../Components/BlockRenderer/BlockRenderer";
import HeroSection from "../Components/Home/Hero/HeroSection";
import HowItWorks from "../Components/Home/HowItWorks/HowItWorks";
import HotListPromo from "../Components/Home/HotListPromo/HotListPromo";
import CategoryGrid from "../Components/Home/CategoryGrid/CategoryGrid";
import Instagram from "../Components/Home/Instagram/Instagram";
import HomeBlogSection from "../Components/Home/HomeBlogSection/HomeBlogSection";
import HomeGallerySection from "../Components/Home/HomeGallerySection/HomeGallerySection";
import NewsletterBlock from "../Components/Home/NewsletterBlock/NewsletterBlock";
import Seo from "../Components/Seo/Seo";
import { useSiteSeo } from "../context/SiteSeoContext";
import { truncateMetaDescription } from "../utils/seoText";
import { usePrerenderReady } from "../utils/prerenderReady";

const DEFAULT_HOT_LIST_PROPS = {
  block1: { tag: "HOT LIST", title: "WOMEN COLLECTION", link: "/shop?gender=female" },
  block2: { tag: "HOT LIST", title: "MEN COLLECTION", link: "/shop?gender=male" },
  block3: { tag: "NEW ARRIVALS", title: "LATEST COLLECTION", link: "/shop" },
  block4: {
    title: "GIFT A RENTAL",
    subtitle: "Surprise someone special — call or WhatsApp us to arrange a rental gift.",
    link: "/contact",
    backgroundColor: "#f5d5c8",
  },
};

function ensureHotListContent(content) {
  if (!Array.isArray(content)) return content;
  return content.map((block) => {
    if (block.type !== "new_season") return block;
    const p = block.props || {};
    return {
      ...block,
      type: "hot_list",
      id: block.id ? String(block.id).replace("new_season", "hot_list") : "hot_list-" + Date.now(),
      props: {
        block1: {
          tag: "HOT LIST",
          title: p.womenTitle || "WOMEN COLLECTION",
          link: "/shop?gender=female",
          ...(p.womenImageUrl && { imageUrl: p.womenImageUrl }),
        },
        block2: {
          tag: "HOT LIST",
          title: p.menTitle || "MEN COLLECTION",
          link: "/shop?gender=male",
          ...(p.menImageUrl && { imageUrl: p.menImageUrl }),
        },
        block3: { tag: "NEW ARRIVALS", title: "LATEST COLLECTION", link: "/shop" },
        block4: {
          title: "GIFT A RENTAL",
          subtitle: "Surprise someone special — call or WhatsApp us to arrange a rental gift.",
          link: "/contact",
          backgroundColor: "#f5d5c8",
        },
      },
    };
  });
}

const Home = () => {
  const [page, setPage] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const siteCtx = useSiteSeo();

  useEffect(() => {
    getPage("home")
      .then((data) => setPage(data))
      .catch(() => setPage(null))
      .finally(() => setLoaded(true));
  }, []);

  usePrerenderReady(loaded);

  const titleSuffix =
    siteCtx.organization_name && String(siteCtx.organization_name).trim()
      ? ` | ${String(siteCtx.organization_name).trim()}`
      : " | Gem Fashion Studios";

  const defaultTitle = `Designer clothing rental${titleSuffix}`;
  const defaultDesc =
    "Rent designer clothing and outfits for every occasion. Browse, book, wear, return — Gem Fashion Studios.";

  const cmsTitle =
    loaded && page?.title && String(page.title).trim()
      ? `${String(page.title).trim()}${titleSuffix}`
      : defaultTitle;
  const cmsDesc =
    loaded && page?.meta_description && String(page.meta_description).trim()
      ? truncateMetaDescription(String(page.meta_description).trim())
      : defaultDesc;

  let inner = null;
  if (loaded && page && Array.isArray(page.content) && page.content.length > 0) {
    const content = ensureHotListContent(page.content);
    const nlIndex = content.findIndex((b) => b.type === "newsletter");
    if (nlIndex >= 0) {
      inner = (
        <>
          <BlockRenderer content={content.slice(0, nlIndex)} />
          <HomeBlogSection />
          <HomeGallerySection />
          <BlockRenderer content={content.slice(nlIndex)} />
        </>
      );
    } else {
      inner = (
        <>
          <BlockRenderer content={content} />
          <HomeBlogSection />
          <HomeGallerySection />
        </>
      );
    }
  } else {
    inner = (
      <>
        <HeroSection />
        <HowItWorks />
        <HotListPromo props={DEFAULT_HOT_LIST_PROPS} />
        <CategoryGrid />
        <Instagram />
        <HomeBlogSection />
        <HomeGallerySection />
        <NewsletterBlock />
      </>
    );
  }

  return (
    <>
      <Seo title={cmsTitle} description={cmsDesc} canonicalPath="/" />
      <h1 className="visually-hidden">Designer Clothing Rental for Every Occasion</h1>
      {inner}
      <section className="homeSeoCopy" aria-labelledby="home-seo-copy-heading">
        <div className="homeSeoCopy__inner">
          <h2 id="home-seo-copy-heading">Rent Designer Outfits for Weddings, Parties and Celebrations</h2>
          <p>
            Gem Fashion Studios makes designer clothing rental simple for every occasion. Choose from
            premium ethnic wear, gowns, lehengas, sherwanis, blazers and party outfits, book your look
            for the dates you need, wear it with confidence, and return it after the event.
          </p>
          <p>
            Explore our <a href="/shop">designer outfit rentals</a>, browse
            <a href="/festive-vibes"> festive wear</a>, find styles by
            <a href="/occasions"> occasion</a>, or <a href="/contact">contact us</a> for help choosing
            the right rental look.
          </p>
        </div>
      </section>
    </>
  );
};

export default Home;
